import { alpha } from "@mui/material";
import { makeStyles } from "@mui/styles";
import configData from "../config";
import axios from "axios";

/**
 * Autocomplete styling configuration.
 *
 * @type {Object}
 */
export const AutocompleteX = {
  display: "inline-block",
  width: "100%",
};

/**
 * Renders a mandatory asterisk.
 *
 * @return {JSX.Element} The mandatory asterisk element.
 */
export const Mandatory = () => {
  return <span className="mandatoryStar">*</span>;
};

/**
 * Returns the waiting time in milliseconds.
 *
 * @return {number} The waiting time in milliseconds.
 */
export const waitingTime = () => {
  return 4000;
};

/**
 * Handles errors from API responses.
 *
 * @param {Object} error - The error object from the API response.
 * @param {Function} setError - The function to set the error message.
 * @return {void}
 */
export function errorHandling(error, setError) {
  if (
    error?.response?.status === 400 &&
    typeof error.response.data === "object"
  ) {
    const data = error.response.data;

    if (data.detail) {
      setError(
        <span className="err_color">
          {data.detail}
        </span>
      );
      return;
    }

    const firstError = Object.values(data)?.[0]?.[0];
    if (firstError) {
      setError(
        <span className="err_color">
          {firstError}
        </span>
      );
      return;
    }
  }

  if (
    error?.response?.status === 500 &&
    typeof error.response.data === "object"
  ) {
    const data = error.response.data;

    if (data.detail) {
      setError(
        <span className="err_color">
          {data.detail}
        </span>
      );
      return;
    }

    const firstError = Object.values(data)?.[0]?.[0];
    if (firstError) {
      setError(
        <span className="err_color">
          {firstError}
        </span>
      );
      return;
    }
  }

  if (error?.response) {
    switch (error.response.status) {
      case 401:
        setError(
          <span className="err_color">
            Session expired or unauthorized access. Please log in again.
          </span>
        );
        break;

      case 404:
        setError(
          <span className="err_color">
            Requested resource not found. Please check and try again.
          </span>
        );
        break;

      case 500:
        setError(
          <span className="err_color">
            Server encountered an error. Please contact support or try again later.
          </span>
        );
        break;

      default:
        setError(
          <span className="err_color">
            Something went wrong. Please try again later.
          </span>
        );
        break;
    }
  }
}


/**
 * Returns the button styles.
 *
 * @param {Object} theme - The theme object.
 * @return {Object} The button styles.
 */
export function ButtonStyles(theme) {
  return {
    background: "#129EA3",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: "500",
    borderRadius: "8px",
    border: "none",
    alignItems: "center",
    boxShadow: `0 4px 20px ${alpha("#558b9aff", 0.2)}`,
    letterSpacing: "1px",
    cursor: "pointer",
    transition: "transform 0.2s ease-in-out",
    margin: "5px",
    gap: "10px",
    bottom: "5px",
  };
}

/**
 * Returns the upload button styles.
 *
 * @return {Object} The upload button styles.
 */
export function UploadButton() {
  return {
    background: "#129EA3",
    color: "#ffffff",
    fontSize: "14px",
    fontWeight: 500,
    borderRadius: "10px",
    boxShadow: `0 4px 20px ${alpha("#558b9aff", 0.2)}`,
    letterSpacing: "1px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    top: "1px",
    bottom: "5px",
    "&:hover": {
      backgroundColor: "#91887e",
    },
  };
}

/**
 * Returns the list items styles.
 *
 * @param {Object} theme - The theme object.
 * @return {Object} The list items styles.
 */
export function ListItemsStyles(theme) {
  return {
    padding: "0px 10px",
    marginTop: "5px",
    borderRadius: "10px",
    background: "#e8f1f7",

    [theme.breakpoints.down("sm")]: {
      padding: "0px 6px",
      marginTop: "3px",
      borderRadius: "6px",
      fontSize: "12px",
    },
    [theme.breakpoints.up("md")]: {
      padding: "0px 12px",
      marginTop: "6px",
      borderRadius: "10px",
      fontSize: "14px",
    },
    [theme.breakpoints.up("lg")]: {
      padding: "0px 16px",
      marginTop: "8px",
      borderRadius: "12px",
      fontSize: "15px",
    },
  };
}

/**
 * Returns the backdrop styles.
 *
 * @return {Object} The backdrop styles.
 */
export function BackdropStyles() {
  return {
    zIndex: 9999,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  };
}

/**
 * Returns the circular progress styles.
 *
 * @return {Object} The circular progress styles.
 */
export function CircularProgressStyles() {
  return {
    zIndex: 10000,
    color: "#314BCA",
  };
}

/**
 * Returns the main report table styles.
 *
 * @param {Object} theme - The theme object.
 * @return {Object} The main report table styles.
 */
export function MainReportTableStyles(theme) {
  return {
    border: "1px solid grey",
    borderRadius: "5px",
    backgroundColor: "#fff",
    overflowY: "hidden",
    flexGrow: 1,
    position: "relative",

    maxHeight: "calc(100vh - 130px)",
    overflowY: "auto",

    "& .MuiTableContainer-root": {
      flex: 1,
      overflow: "auto",
      minHeight: 0,
    },

    "&::-webkit-scrollbar": {
      width: 6,
      height: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#C1C1C1",
      borderRadius: 4,
    },
  };
}

/**
 * Returns the report table styles.
 *
 * @param {Object} theme - The theme object.
 * @return {Object} The report table styles.
 */
export function ReportTableStyles(theme) {
  return {
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    backgroundColor: "#fff",

    display: "flex",
    flexDirection: "column",

    position: "relative",

    maxHeight: "calc(100vh - 130px)",

    "& .MuiTableContainer-root": {
      flex: 1,
      overflow: "auto",
      minHeight: 0,
    },

    "&::-webkit-scrollbar": {
      width: 6,
      height: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#C1C1C1",
      borderRadius: 4,
    },
  };
}

/**
 * Returns the audit report table styles.
 *
 * @param {Object} theme - The theme object.
 * @return {Object} The audit report table styles.
 */
export function AuditReportTableStyles(theme) {
  return {
    border: "1px solid grey",
    borderRadius: "5px",
    backgroundColor: "#fff",
    flexGrow: 1,
    position: "relative",

    maxHeight: "calc(100vh - 250px)",
    overflowY: "auto",

    scrollbarWidth: "none",
    msOverflowStyle: "none",

    "&::-webkit-scrollbar": {
      display: "none",
    },

    [theme.breakpoints.down("sm")]: {
      maxHeight: "calc(100vh - 160px)",
      borderRadius: "3px",
    },

    [theme.breakpoints.up("md")]: {
      maxHeight: "calc(100vh - 180px)",
    },

    [theme.breakpoints.up("lg")]: {
      maxHeight: "calc(100vh - 140px)",
    },

    [theme.breakpoints.up("xl")]: {
      maxHeight: "calc(100vh - 100px)",
    },
  };
}

/**
 * Returns the matched report table styles.
 *
 * @param {Object} theme - The theme object.
 * @return {Object} The matched report table styles.
 */
export function MacthedReportTableStyles(theme) {
  return {
    border: "1px solid grey",
    borderRadius: "5px",
    backgroundColor: "#fff",
    overflowY: "hidden",
    flexGrow: 1,
    position: "relative",

    maxHeight: "calc(100vh - 220px)",
    overflowY: "auto",

    "& .MuiTableContainer-root": {
      flex: 1,
      overflow: "auto",
      minHeight: 0,
    },

    "&::-webkit-scrollbar": {
      width: 6,
      height: 6,
    },
    "&::-webkit-scrollbar-thumb": {
      background: "#C1C1C1",
      borderRadius: 4,
    },
  };
}

/**
 * Returns the table header styles.
 *
 * @param {Object} theme - The theme object.
 * @return {Object} The table header styles.
 */
export function TableHeaderStyles(theme) {
  return {
    textAlign: "center",
    fontSize: "17px",
    width: "auto",
    fontWeight: "600",
    backgroundColor: "#6eb1f5ff",
    color: "#000",
    border: "1px solid #575757",

    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
      padding: "4px",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "15px",
      padding: "6px",
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "18px",
      padding: "8px",
    },
  };
}

/**
 * Returns the dialog styles.
 *
 * @param {Object} theme - The theme object.
 * @return {Object} The dialog styles.
 */
export function DialogStyles(theme) {
  return {
    "& .MuiDialog-paper": {
      width: "100%",
      borderRadius: "8px",
      backgroundColor: "#fff",

      [theme.breakpoints.down("sm")]: {
        width: "95%",
        borderRadius: "6px",
      },
      [theme.breakpoints.up("md")]: {
        width: "80%",
      },
      [theme.breakpoints.up("lg")]: {
        width: "60%",
      },
    },
  };
}

/**
 * Handles API errors.
 *
 * @param {Object} error - The error object.
 * @param {Function} setErrorMessage - The function to set the error message.
 * @param {Function} setErrorDialog - The function to set the error dialog.
 * @param {Function} setErrorType - The function to set the error type.
 * @return {Object} The retry object.
 */
export function handleApiError(error, setErrorMessage, setErrorDialog, setErrorType) {
  console.error("API Error:", error);

  if (error.code === "ECONNABORTED") {
    return { retry: true };
  }

  if (!error.response) {
    if (!navigator.onLine) {
      setErrorMessage(<span className="err_color">Network connection lost. Please check your internet.</span>);
      setErrorType("network");
    } else {
      setErrorMessage(<span className="err_color">Unable to reach the server at the moment. Please try again shortly or contact the administrator.</span>);
      setErrorType("server");
    }
    setErrorDialog(true);
    return;
  }

  if (error.response.status >= 500) {
    setErrorMessage(<span className="err_color">The server encountered an issue while processing your request. Please try again or contact the administrator.</span>);
    setErrorType("server");
    setErrorDialog(true);
    return;
  }

  setErrorMessage(<span className="err_color">Request failed due to a server issue. Please try again or contact the administrator.</span>);
  setErrorType("server");
  setErrorDialog(true);
}

/**
 * Handles edit with lock.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.module - The module name.
 * @param {Object} params.item - The item object.
 * @param {Function} params.onSuccess - The success callback function.
 * @param {Function} params.onLocked - The locked callback function.
 * @return {Promise<void>}
 */
export const handleEditWithLock = async ({
  module,
  item,
  onSuccess,
  onLocked,
}) => {
  try {
    const res = await axios.post(
      `${configData.API_SERVER}handle-edit-lock/`,
      {
        module,
        record_id: item.jd_id || item.id,
      }
    );

    if (res.data.status === "locked") {
      onLocked?.(res.data);
      return;
    }

    onSuccess(item);

  } catch (err) {
    console.error(err);
    onLocked?.({ message: "Error while locking" });
  }
};

/**
 * Releases the edit lock.
 *
 * @param {string} module - The module name.
 * @param {number} id - The record ID.
 * @return {Promise<void>}
 */
export const releaseLock = async (module, id) => {
  try {
    await axios.post(
      `${configData.API_SERVER}release-edit-lock/`,
      {
        module,
        record_id: id,
      }
    );
  } catch (err) {
    console.error("Release lock failed", err);
  }
};

/**
 * Enables content protection.
 *
 * @return {void}
 */
export const enableContentProtection = () => {

    const disableRightClick = (e) => {
        e.preventDefault();
    };

    const handleFocus = () => {
        document.body.style.filter = "none";
    };

    const disableKeys = (e) => {

        if (e.ctrlKey && e.key.toLowerCase() === "c") {
            e.preventDefault();
        }

        if (e.ctrlKey && e.key.toLowerCase() === "x") {
            e.preventDefault();
        }

        if (e.ctrlKey && e.key.toLowerCase() === "a") {
            e.preventDefault();
        }

        if (e.ctrlKey && e.key.toLowerCase() === "p") {
            e.preventDefault();
        }

        if (e.key === "F12") {
            e.preventDefault();
        }

        if (e.key === "PrintScreen") {

            navigator.clipboard.writeText("");

            document.body.style.filter = "blur(20px)";

            setTimeout(() => {
                document.body.style.filter = "none";
            }, 2000);

            e.preventDefault();
        }

        if (
            e.metaKey &&
            e.shiftKey &&
            e.key.toLowerCase() === "s"
        ) {
            e.preventDefault();
        }

        if (
            e.ctrlKey &&
            e.shiftKey &&
            ["I", "C", "U"].includes(e.key.toUpperCase())
        ) {
            e.preventDefault();
        }
    };

    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";

    document.addEventListener("contextmenu", disableRightClick);
    document.addEventListener("keydown", disableKeys);

    window.addEventListener("focus", handleFocus);

    return () => {

        document.removeEventListener("contextmenu", disableRightClick);
        document.removeEventListener("keydown", disableKeys);

        window.removeEventListener("focus", handleFocus);

        document.body.style.userSelect = "auto";
        document.body.style.webkitUserSelect = "auto";
        document.body.style.filter = "none";
    };
};